/**
 * AI Recruit - Resume Formatter Templates - Client Picker Input
 * A combobox for the "Generate New Resume Template" / "Regenerate" modal's
 * Client field - same searchable-dropdown look as Jobs'
 * SearchableClientDropdown, but editable (typing a brand-new client name
 * that isn't in `options` yet is still allowed) rather than a strict
 * pick-one-from-the-list filter.
 */

import React, { useEffect, useRef, useState } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'

interface ClientPickerInputProps {
  value: string
  onChange: (value: string) => void
  options: string[]
  placeholder?: string
}

const ClientPickerInput: React.FC<ClientPickerInputProps> = ({ value, onChange, options, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const filteredOptions = value.trim()
    ? options.filter((c) => c.toLowerCase().includes(value.trim().toLowerCase()))
    : options

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-9 text-sm focus:border-primary-500 focus:ring-primary-500"
        />
        <button
          type="button"
          onClick={() => setIsOpen((open) => !open)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          aria-label="Show client suggestions"
        >
          <ChevronDownIcon className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((c) => (
              <button
                type="button"
                key={c}
                onClick={() => {
                  onChange(c)
                  setIsOpen(false)
                }}
                className={`block w-full truncate px-3 py-1.5 text-left text-sm hover:bg-gray-50 ${
                  c === value ? 'bg-primary-50 font-medium text-primary-700' : 'text-gray-700'
                }`}
              >
                {c}
              </button>
            ))
          ) : (
            <p className="px-3 py-1.5 text-sm italic text-gray-400">No matching client - keep typing to add a new one.</p>
          )}
        </div>
      )}
    </div>
  )
}

export default ClientPickerInput
